import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search, Calendar, Truck, MapPin, Clock, FileSpreadsheet, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_COLORS = {
  'Lunes': 'bg-blue-100 text-blue-800 border-blue-300',
  'Martes': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Miercoles': 'bg-amber-100 text-amber-800 border-amber-300',
  'Jueves': 'bg-purple-100 text-purple-800 border-purple-300',
  'Viernes': 'bg-rose-100 text-rose-800 border-rose-300',
  'Sabado': 'bg-cyan-100 text-cyan-800 border-cyan-300',
  'Domingo': 'bg-slate-100 text-slate-800 border-slate-300',
};

export default function ProviderVisits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = user?.role === 'admin' || (user?.permissions || []).includes('exportar_reportes');
  const [exporting, setExporting] = useState('');

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (providerSearch.trim()) params.append('provider_name', providerSearch.trim());
      if (storeFilter !== 'all') params.append('store_id', storeFilter);
      const res = await axios.get(`${API}/reports/provider-visit-days/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dias_visita_proveedores.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF descargado' : 'Excel descargado');
    } catch (e) {
      toast.error('No se pudo descargar');
    } finally { setExporting(''); }
  };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [providerSearch, setProviderSearch] = useState('');
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stores`).then(res => setStores(res.data || [])).catch(() => {});
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (providerSearch.trim()) params.append('provider_name', providerSearch.trim());
      if (storeFilter !== 'all') params.append('store_id', storeFilter);
      const res = await axios.get(`${API}/reports/provider-visit-days?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para ver este reporte');
      } else {
        toast.error('Error al cargar reporte');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b border-slate-200 p-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Dias de Visita por Proveedor</h1>
            <p className="text-sm text-slate-500">Basado en fechas de pago reportadas (dia de visita = dia de pago - 1)</p>
          </div>
          {canExport && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => exportReport('excel')} disabled={!!exporting} className="text-emerald-700 border-emerald-300" data-testid="visit-export-excel">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> {exporting === 'excel' ? 'Generando…' : 'Excel'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportReport('pdf')} disabled={!!exporting} className="text-red-700 border-red-300" data-testid="visit-export-pdf">
                <FileText className="h-4 w-4 mr-1" /> {exporting === 'pdf' ? 'Generando…' : 'PDF'}
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Filters */}
        <Card className="p-4 border-l-4 border-indigo-500" data-testid="visit-filters">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
            <Search className="mr-2 h-5 w-5 text-[#2c57c5]" />
            Filtros
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="text-sm">Proveedor</Label>
              <Input
                placeholder="Buscar proveedor..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchReport()}
                className="mt-1"
                data-testid="visit-provider-search"
              />
            </div>
            <div>
              <Label className="text-sm">Sucursal</Label>
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="mt-1" data-testid="visit-store-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Desde</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="visit-start-date" />
            </div>
            <div>
              <Label className="text-sm">Hasta</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" data-testid="visit-end-date" />
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchReport} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white flex-1" data-testid="visit-search-btn">
                <Search className="mr-2 h-4 w-4" /> Buscar
              </Button>
              <Button variant="outline" onClick={() => { setProviderSearch(''); setStoreFilter('all'); setStartDate(''); setEndDate(''); setTimeout(fetchReport, 100); }} data-testid="visit-clear-btn">
                Limpiar
              </Button>
            </div>
          </div>
        </Card>

        {loading && (
          <Card className="p-8 text-center"><p className="text-slate-600">Cargando reporte...</p></Card>
        )}

        {data && !loading && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card className="p-4 bg-indigo-50 border-indigo-200">
                <div className="flex items-center gap-3">
                  <Truck className="h-8 w-8 text-indigo-400" />
                  <div>
                    <p className="text-xs text-slate-500">Combinaciones Proveedor/Tienda</p>
                    <p className="text-2xl font-bold text-[#1e3a8a]" data-testid="visit-total-combos">{data.total_proveedores}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-emerald-400" />
                  <div>
                    <p className="text-xs text-slate-500">Total Pagos Analizados</p>
                    <p className="text-2xl font-bold text-[#1e3a8a]" data-testid="visit-total-payments">{data.total_pagos}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-3">
                  <MapPin className="h-8 w-8 text-amber-400" />
                  <div>
                    <p className="text-xs text-slate-500">Tiendas Involucradas</p>
                    <p className="text-2xl font-bold text-amber-700">{new Set(data.data.map(r => r.tienda)).size}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Results Table */}
            <Card className="p-6 border-l-4 border-indigo-500" data-testid="visit-results">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
                <Clock className="mr-2 h-5 w-5 text-[#2c57c5]" />
                Dias de Pago y Visita Estimada
              </h3>

              {data.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-indigo-50">
                        <th className="text-left p-3 font-semibold">Proveedor</th>
                        <th className="text-left p-3 font-semibold">Tienda</th>
                        <th className="text-center p-3 font-semibold">Pagos</th>
                        <th className="text-right p-3 font-semibold">Monto Total</th>
                        <th className="text-left p-3 font-semibold">Dias de Pago</th>
                        <th className="text-left p-3 font-semibold">Dias de Visita (estimado)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.data.map((row, idx) => (
                        <React.Fragment key={idx}>
                          <tr
                            className={`border-b hover:bg-slate-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                            onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                            data-testid={`visit-row-${idx}`}
                          >
                            <td className="p-3 font-medium">{row.proveedor}</td>
                            <td className="p-3 text-slate-600">{row.tienda || '-'}</td>
                            <td className="p-3 text-center">
                              <Badge className="bg-indigo-100 text-[#1e3a8a]">{row.total_pagos}</Badge>
                            </td>
                            <td className="p-3 text-right font-semibold">Q{row.monto_total.toLocaleString('es-GT', {minimumFractionDigits: 2})}</td>
                            <td className="p-3">
                              <div className="flex flex-wrap gap-1">
                                {row.dias.map((d, i) => (
                                  <Badge key={i} variant="outline" className={`text-xs ${DAY_COLORS[d.dia_pago] || 'bg-slate-100'}`}>
                                    {d.dia_pago} ({d.frecuencia})
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex flex-wrap gap-1">
                                {row.dias.map((d, i) => (
                                  <Badge key={i} className={`text-xs font-bold ${DAY_COLORS[d.dia_visita] || 'bg-slate-100'}`}>
                                    {d.dia_visita}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                          {expandedRow === idx && (
                            <tr className="bg-indigo-50/50">
                              <td colSpan={6} className="p-4">
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-sm text-slate-700">Detalle de frecuencia por dia:</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {row.dias.map((d, i) => (
                                      <div key={i} className="bg-white rounded-lg p-3 border border-slate-200">
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="font-medium text-sm">{d.dia_pago}</span>
                                          <Badge variant="outline" className="text-xs">{d.frecuencia} pagos</Badge>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-slate-500">
                                          <Truck className="h-3 w-3" />
                                          <span>Visita estimada: <strong className="text-[#1e3a8a]">{d.dia_visita}</strong></span>
                                        </div>
                                        <div className="mt-1 bg-indigo-100 rounded-full h-2">
                                          <div
                                            className="bg-indigo-500 rounded-full h-2"
                                            style={{width: `${Math.min(100, (d.frecuencia / row.total_pagos) * 100)}%`}}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {row.fechas_pago && row.fechas_pago.length > 0 && (
                                    <div>
                                      <h4 className="font-semibold text-sm text-slate-700 mb-1">Ultimas fechas de pago:</h4>
                                      <div className="flex flex-wrap gap-1">
                                        {row.fechas_pago.map((f, i) => (
                                          <Badge key={i} variant="outline" className="text-xs bg-white">{f}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No se encontraron datos para los filtros seleccionados</p>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
